import { useState, useRef, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Plus, ArrowRight, ShieldCheck } from "lucide-react";
import { SERVICES, SERVICE_IMAGES } from "../data/content";
import { scrollToId } from "../lib/scroll";
import { Kicker } from "./fx";

const EASE = [0.22, 1, 0.36, 1];

export default function Services() {
  const [open, setOpen] = useState(0);
  const [hovered, setHovered] = useState(null);
  const listRef = useRef(null);

  useEffect(() => {
    SERVICE_IMAGES.forEach((src) => {
      const img = new Image();
      img.src = src;
    });
  }, []);

  const active = hovered ?? (open < 0 ? 0 : open);
  const activeImg = SERVICE_IMAGES[active % SERVICE_IMAGES.length];
  const activeName = SERVICES[active]?.name || "Treatment Atelier";

  const book = (serviceName) => {
    window.dispatchEvent(
      new CustomEvent("ndc:select-service", { detail: serviceName })
    );
    scrollToId("#book");
  };

  const onListMouseMove = (e) => {
    const el = listRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    el.style.setProperty("--mx", `${e.clientX - r.left}px`);
    el.style.setProperty("--my", `${e.clientY - r.top}px`);
  };

  return (
    <section id="services" className="relative bg-[#070B12] py-24 sm:py-32">
      <div
        aria-hidden
        className="pointer-events-none absolute right-0 top-24 h-[380px] w-[380px] rounded-full bg-toothcyan/[0.06] blur-3xl"
      />
      <div className="relative mx-auto max-w-7xl px-5 sm:px-8">
        <motion.div
          initial={{ opacity: 0, y: 26 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, ease: EASE }}
          className="mb-14 flex flex-col justify-between gap-6 lg:flex-row lg:items-end"
        >
          <div className="max-w-2xl">
            <Kicker>What We Treat</Kicker>
            <h2 className="font-serif text-3xl font-light tracking-tight text-white sm:text-5xl">
              The treatment atelier —{" "}
              <em className="italic text-ember">eleven disciplines.</em>
            </h2>
          </div>
          <p className="max-w-sm text-sm leading-relaxed text-mist lg:pb-2 lg:text-right">
            Glide over a discipline to preview it. Tap to unfold the clinical
            breakdown — then reserve your chair in one click.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 gap-10 lg:grid-cols-12">
          <motion.div
            initial={{
              opacity: 0,
              clipPath: "polygon(0 12%, 100% 0%, 100% 88%, 0 100%)",
            }}
            whileInView={{ opacity: 1, clipPath: "polygon(0 0%, 100% 0%, 100% 100%, 0 100%)" }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 1.1, ease: EASE }}
            className="lg:col-span-4"
          >
            <div className="lg:sticky lg:top-28">
              <div className="relative h-72 overflow-hidden rounded-[2rem] shadow-[0_25px_70px_rgba(0,0,0,0.55)] ring-1 ring-white/10 sm:h-[460px]">
                <AnimatePresence mode="sync">
                  <motion.img
                    key={activeImg}
                    src={activeImg}
                    alt={activeName}
                    data-testid="services-feature-image"
                    loading="eager"
                    initial={{ opacity: 0, scale: 1.04 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.3, ease: "easeOut" }}
                    className="absolute inset-0 h-full w-full object-cover"
                  />
                </AnimatePresence>
                <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-[#070B12]/95 via-[#070B12]/35 to-transparent p-6 pt-24">
                  <div className="font-mono text-[9px] uppercase tracking-[0.24em] text-toothcyan">
                    Now Previewing
                  </div>
                  <div className="mt-1 font-serif text-xl font-semibold text-ivory">
                    {activeName}
                  </div>
                </div>
                <div className="absolute left-5 top-5 flex items-center gap-2 rounded-full border border-white/20 bg-[#070B12]/60 px-3.5 py-1.5 backdrop-blur">
                  <ShieldCheck className="h-3.5 w-3.5 text-ember" />
                  <span className="font-mono text-[9px] uppercase tracking-[0.16em] text-ivory/90">
                    Consultation-first guarantee
                  </span>
                </div>
              </div>
              <p className="mt-5 hidden text-xs leading-relaxed text-mist lg:block">
                Every plan at Narayan Dental Care begins with a digital
                diagnosis and a transparent, no-surprises consultation with Dr.
                Panda.
              </p>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.9, delay: 0.1, ease: EASE }}
            className="lg:col-span-8"
          >
            <div
              ref={listRef}
              onMouseMove={onListMouseMove}
              data-testid="services-index-list"
              className="spotlight-list border-y border-white/10"
            >
              {SERVICES.map((s, i) => {
                const isOpen = open === i;
                return (
                  <div
                    key={s.name}
                    className={`relative border-b border-white/10 transition-colors duration-300 last:border-b-0 ${
                      isOpen ? "bg-[#0F192C]" : "hover:bg-[#0F192C]/70"
                    }`}
                    onMouseEnter={() => setHovered(i)}
                    onMouseLeave={() => setHovered(null)}
                  >
                    <button
                      data-testid={`service-row-${i + 1}`}
                      onClick={() => setOpen(isOpen ? -1 : i)}
                      className="group flex w-full items-center gap-4 px-4 py-5 text-left sm:gap-7 sm:px-6 sm:py-6"
                    >
                      <span
                        className={`w-10 shrink-0 font-serif text-2xl font-semibold transition-colors duration-300 sm:text-3xl ${
                          isOpen
                            ? "text-toothcyan"
                            : "text-mist/40 group-hover:text-toothcyan"
                        }`}
                      >
                        {String(i + 1).padStart(2, "0")}
                      </span>
                      <span className="flex-1">
                        <span
                          className={`block font-serif text-xl font-semibold tracking-tight transition-all duration-300 sm:text-2xl ${
                            isOpen
                              ? "text-white"
                              : "text-slate-200 group-hover:translate-x-1.5 group-hover:text-white"
                          }`}
                        >
                          {s.name}
                        </span>
                        <span className="mt-1 block font-mono text-[10px] uppercase tracking-[0.2em] text-mist">
                          {s.tag}
                        </span>
                      </span>
                      <span
                        className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-full border transition-all duration-300 ${
                          isOpen
                            ? "rotate-45 border-ember bg-ember text-[#070B12] shadow-[0_0_18px_rgba(232,178,92,0.4)]"
                            : "border-white/10 text-slate-300 group-hover:border-ember/60 group-hover:text-ember"
                        }`}
                      >
                        <Plus className="h-4 w-4" />
                      </span>
                    </button>

                    <AnimatePresence initial={false}>
                      {isOpen && (
                        <motion.div
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: "auto", opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          transition={{ duration: 0.35, ease: EASE }}
                          className="overflow-hidden"
                        >
                          <div className="flex flex-col gap-5 px-4 pb-6 pl-[4.5rem] pr-6 sm:flex-row sm:items-end sm:justify-between sm:pl-[6rem]">
                            <p className="max-w-md text-sm leading-relaxed text-slate-400">
                              {s.desc}
                            </p>
                            <button
                              data-testid={`service-row-${i + 1}-book-button`}
                              onClick={() => book(s.name)}
                              className="group/btn inline-flex shrink-0 items-center gap-2 self-start rounded-full border border-ember/50 bg-ember/10 px-5 py-2.5 text-xs font-bold text-ember transition-all duration-200 hover:bg-ember hover:text-[#070B12] active:scale-[0.97] sm:self-auto"
                            >
                              Book this treatment
                              <ArrowRight className="h-3.5 w-3.5 transition-transform duration-300 group-hover/btn:translate-x-0.5" />
                            </button>
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                );
              })}
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
